// POKERCITY DEALER CERTIFICATION — QUESTION BANK
// Track: Professional | Category: Dealing Procedure
// 35 questions | Difficulty: E=Easy, M=Medium, H=Hard

export const DEALING_PROCEDURE_QUESTIONS = [

  // ── EASY (7 questions) ──────────────────────────────────────────────────

  {
    id: 'dp_001', track: 'professional', category: 'dealing_procedure', difficulty: 'easy',
    question: 'In what order does the dealer pitch cards in a standard Texas Hold\'em game?',
    options: [
      'A) Starting with the player to the dealer\'s right, clockwise',
      'B) Starting with the small blind, clockwise',
      'C) Starting with the big blind, clockwise',
      'D) Starting with the player to the dealer\'s left, counter-clockwise'
    ],
    correct: 'B',
    explanation: 'Cards are dealt starting with the small blind and proceeding clockwise. Each player receives one card per pass until all active players have their hole cards.'
  },

  {
    id: 'dp_002', track: 'professional', category: 'dealing_procedure', difficulty: 'easy',
    question: 'How many cards are burned before the flop is dealt?',
    options: [
      'A) None — burn cards only apply to the turn and river',
      'B) One card is burned before the flop',
      'C) Two cards are burned before the flop',
      'D) The number of burn cards equals the number of active players'
    ],
    correct: 'B',
    explanation: 'One card is burned before each street — before the flop, before the turn, and before the river. Burn cards protect against marked cards being seen by players.'
  },

  {
    id: 'dp_003', track: 'professional', category: 'dealing_procedure', difficulty: 'easy',
    question: 'When should the dealer shuffle the deck?',
    options: [
      'A) Only at the start of a new session',
      'B) Before every hand, including a riffle, strip, and riffle sequence',
      'C) Only when requested by a player',
      'D) Every five hands as standard procedure'
    ],
    correct: 'B',
    explanation: 'The deck is shuffled before every hand. A standard casino shuffle includes multiple riffles, a strip (cutting the deck into packets), and a final riffle. This ensures adequate randomization.'
  },

  {
    id: 'dp_004', track: 'professional', category: 'dealing_procedure', difficulty: 'easy',
    question: 'Where should the dealer place the burn card?',
    options: [
      'A) Face-up on top of the deck',
      'B) Face-down under the pot or to the side of the board cards',
      'C) In a separate discard pile visible to all players',
      'D) Under the dealer button'
    ],
    correct: 'B',
    explanation: 'Burn cards are placed face-down under the pot or beside the board, separate from the deck and away from the muck. This keeps them available for review in case of a dispute.'
  },
  {
    id: 'dp_006', track: 'professional', category: 'dealing_procedure', difficulty: 'easy',
    question: 'How many community cards are on the board in a complete Texas Hold\'em hand?',
    options: [
      'A) Three',
      'B) Four',
      'C) Five',
      'D) Six'
    ],
    correct: 'C',
    explanation: 'Five community cards are dealt: three on the flop, one on the turn, one on the river. Players use any combination of hole cards and community cards to make the best five-card hand.'
  },

  {
    id: 'dp_007', track: 'professional', category: 'dealing_procedure', difficulty: 'easy',
    question: 'When is it appropriate for the dealer to table (spread) the flop?',
    options: [
      'A) As soon as the pre-flop betting round ends',
      'B) After burning one card and all pre-flop action is complete',
      'C) Only when all players request it simultaneously',
      'D) Immediately after the last blind is posted'
    ],
    correct: 'B',
    explanation: 'The flop is dealt after all pre-flop betting is complete. The dealer burns one card then deals three community cards face-up simultaneously, spreading them in a line.'
  },

  // ── MEDIUM (16 questions) ───────────────────────────────────────────────

  {
    id: 'dp_008', track: 'professional', category: 'dealing_procedure', difficulty: 'medium',
    question: 'A player takes an unusually long time to act. Another player calls for a clock. What is the dealer\'s role?',
    options: [
      'A) The dealer starts counting immediately — no floor needed',
      'B) The dealer notifies the floor that a clock has been called; the floor manages the countdown',
      'C) The dealer ignores clock requests — only the TD can call a clock',
      'D) The dealer gives the player one more minute then folds their hand'
    ],
    correct: 'B',
    explanation: 'When a player calls for a clock, the dealer notifies the floor. The floor comes to the table, gives the player a set time (typically 60 seconds with a 10-second warning), and manages the countdown. The dealer does not unilaterally start a clock.'
  },

  {
    id: 'dp_009', track: 'professional', category: 'dealing_procedure', difficulty: 'medium',
    question: 'What is the correct procedure for dealing to a player who has stepped away from the table but whose hand will be live?',
    options: [
      'A) Skip their seat — absent players do not receive cards',
      'B) Deal their cards face-down to their seat; the hand will be folded automatically when action reaches them',
      'C) Deal their cards and hold them until the player returns',
      'D) Ask the table whether to deal the absent player in'
    ],
    correct: 'B',
    explanation: 'In tournament play, an absent player\'s cards are dealt face-down to their seat. When action reaches their hand, it is folded. Blinds are still posted. The player cannot act if not present.'
  },

  {
    id: 'dp_010', track: 'professional', category: 'dealing_procedure', difficulty: 'medium',
    question: 'After the river is dealt and betting is complete, in what order are hands shown at showdown?',
    options: [
      'A) Clockwise from the dealer button',
      'B) The last aggressor shows first; if no bet on the river, the first player to the left of the dealer shows first',
      'C) The player with the most chips shows first',
      'D) All players show simultaneously'
    ],
    correct: 'B',
    explanation: 'At showdown, the last aggressive action (bet or raise) on the final street shows first. If the river was checked around, the first active player to the left of the button shows first, then proceeding clockwise.'
  },

  {
    id: 'dp_011', track: 'professional', category: 'dealing_procedure', difficulty: 'medium',
    question: 'A player\'s chip accidentally falls into the pot during a hand. What do you do?',
    options: [
      'A) Leave it — the player made their bet',
      'B) Return it immediately before any further action',
      'C) Add it to the pot — it is now part of the bet',
      'D) Call the floor to determine if it was intentional'
    ],
    correct: 'B',
    explanation: 'Chips that accidentally fall into the pot should be returned to the player immediately. Accidental motions are not binding. However, if the dealer cannot determine it was accidental versus intentional, the floor should be called.'
  },

  {
    id: 'dp_012', track: 'professional', category: 'dealing_procedure', difficulty: 'medium',
    question: 'During the deal, you notice a player has one too many cards in their hand. What do you do?',
    options: [
      'A) Discretely remove a card and continue dealing',
      'B) Stop the deal immediately and call the floor',
      'C) Complete the deal and sort out the extra card afterward',
      'D) Ask a player to identify which card they should have received'
    ],
    correct: 'B',
    explanation: 'Any irregularity during the deal should be stopped immediately. The dealer calls the floor to determine whether a misdeal has occurred. Continuing with an incorrect deal compounds the problem.'
  },

  {
    id: 'dp_013', track: 'professional', category: 'dealing_procedure', difficulty: 'medium',
    question: 'What is the dealer\'s responsibility regarding pot management throughout a hand?',
    options: [
      'A) Only to push the final pot to the winner',
      'B) To track the total pot size and announce it when asked, and to ensure correct bet amounts are in the pot',
      'C) To count chips only at showdown',
      'D) Pot management is the players\' responsibility; the dealer only deals cards'
    ],
    correct: 'B',
    explanation: 'The dealer is responsible for accurate pot management throughout the hand — confirming bet amounts are correct, keeping the pot organized and countable, and announcing the pot size when requested. This protects all players.'
  },

  {
    id: 'dp_014', track: 'professional', category: 'dealing_procedure', difficulty: 'medium',
    question: 'A new deck is brought to the table. What is the correct inspection procedure?',
    options: [
      'A) Remove from packaging and shuffle immediately',
      'B) Spread face-up to verify all 52 cards are present and undamaged, then shuffle',
      'C) Count the cards face-down, then shuffle',
      'D) Spread face-down, then flip face-up for one player to verify'
    ],
    correct: 'B',
    explanation: 'A new deck should be spread face-up on the table so all players can verify the full 52-card deck is present and no cards are marked or damaged. Then the deck is shuffled.'
  },

  {
    id: 'dp_015', track: 'professional', category: 'dealing_procedure', difficulty: 'medium',
    question: 'How do you handle a situation where a player continuously acts out of turn?',
    options: [
      'A) Ignore it — it is the other players\' responsibility to act in order',
      'B) Verbally remind the player once; on repeat violations notify the floor',
      'C) Penalize the player immediately by folding their hand',
      'D) Ask the table to vote on whether to penalize the player'
    ],
    correct: 'B',
    explanation: 'The dealer should verbally remind a player who acts out of turn. If the behavior continues, the floor should be notified. The dealer does not have authority to penalize players — that is the floor\'s responsibility.'
  },

  {
    id: 'dp_016', track: 'professional', category: 'dealing_procedure', difficulty: 'medium',
    question: 'A player asks to see a mucked hand from a previous street. Can the dealer show it?',
    options: [
      'A) Yes — any player may request to see any mucked hand',
      'B) No — mucked hands are dead and not shown',
      'C) Only the last caller has the right to see a called mucked hand at showdown',
      'D) Only the floor can authorize showing a mucked hand'
    ],
    correct: 'C',
    explanation: 'In most rule sets, only the player who called the final bet has the right to see the called hand at showdown, and only if requested. This right is not unlimited — it applies to the final called hand, not any mucked hand during the street.'
  },

  {
    id: 'dp_017', track: 'professional', category: 'dealing_procedure', difficulty: 'medium',
    question: 'During a hand, a player\'s phone rings loudly and they answer it at the table. What do you do?',
    options: [
      'A) Nothing — phone calls are not a dealer concern',
      'B) Ask the player to end the call; if they refuse, notify the floor',
      'C) Immediately fold the player\'s hand as a penalty',
      'D) Continue dealing and let other players object if they choose'
    ],
    correct: 'B',
    explanation: 'Phone use during a hand is generally prohibited in tournament poker. The dealer should politely ask the player to end the call. If the player refuses, the floor is notified. The dealer does not fold hands unilaterally as a conduct penalty.'
  },

  {
    id: 'dp_018', track: 'professional', category: 'dealing_procedure', difficulty: 'medium',
    question: 'You push the pot to the wrong player. The error is noticed before the next hand begins. What happens?',
    options: [
      'A) The pot cannot be corrected once it has been pushed',
      'B) Stop action immediately, retrieve the pot, and award it to the correct player',
      'C) The floor is called and they decide based on a review',
      'D) The dealer takes the pot back only if the incorrect player agrees'
    ],
    correct: 'B',
    explanation: 'If a pot is pushed to the wrong player and discovered before the next hand begins, the dealer stops action and corrects the award immediately. The incorrect player has no right to keep a pot they did not win.'
  },

  {
    id: 'dp_019', track: 'professional', category: 'dealing_procedure', difficulty: 'medium',
    question: 'What should the dealer do if they are unsure about the correct ruling in a situation?',
    options: [
      'A) Make a ruling and move on to keep the game flowing',
      'B) Ask the table to vote on the correct ruling',
      'C) Call the floor immediately — dealers do not make rulings',
      'D) Apply the rule that benefits the most players'
    ],
    correct: 'C',
    explanation: 'Dealers deal cards and manage the physical game. Rulings are the floor\'s responsibility. When in doubt, the dealer calls the floor. Making incorrect rulings causes disputes and damages game integrity.'
  },

  {
    id: 'dp_020', track: 'professional', category: 'dealing_procedure', difficulty: 'medium',
    question: 'How should a dealer handle a player who is verbally abusive toward them?',
    options: [
      'A) Respond in kind to establish authority',
      'B) Remain calm, do not engage with the abuse, and notify the floor',
      'C) Ask the other players if they want the abusive player removed',
      'D) Stop dealing until the player apologizes'
    ],
    correct: 'B',
    explanation: 'Dealers maintain professionalism regardless of player behavior. The dealer remains calm, does not engage with or escalate the confrontation, and notifies the floor. The floor handles conduct issues. A dealer who retaliates damages their credibility.'
  },

  {
    id: 'dp_021', track: 'professional', category: 'dealing_procedure', difficulty: 'medium',
    question: 'A player requests a deck change. Can they demand this at any time?',
    options: [
      'A) Yes — any player may demand a new deck at any time',
      'B) Players may request a deck change but the floor decides whether to honor it',
      'C) Only the chip leader may request a deck change',
      'D) Deck changes are only done at scheduled breaks'
    ],
    correct: 'B',
    explanation: 'Players may request a deck change and the floor determines whether to honor the request. Reasonable requests (concern about marked cards, damaged deck) are typically honored. Dealers bring the request to the floor\'s attention.'
  },

  {
    id: 'dp_022', track: 'professional', category: 'dealing_procedure', difficulty: 'medium',
    question: 'What is the correct way for a dealer to announce the winning hand at showdown?',
    options: [
      'A) Simply push the pot without announcement',
      'B) Announce the winning hand clearly (e.g. "Full house, aces full of kings wins")',
      'C) Ask players to announce their own hands',
      'D) Only announce if there is a dispute'
    ],
    correct: 'B',
    explanation: 'The dealer should clearly announce the winning hand at showdown (e.g. "Flush, ace high wins"). This protects all players, ensures transparency, and prevents disputes about what hand was shown.'
  },

  {
    id: 'dp_023', track: 'professional', category: 'dealing_procedure', difficulty: 'medium',
    question: 'A player in early position posts their blind and then immediately leaves the table. Preflop action folds around to the big blind. What happens to the early position player\'s blind?',
    options: [
      'A) The blind is returned to the absent player',
      'B) The blind stays in the pot; the hand is dealt to their seat and folded when action reaches them',
      'C) The blind is added to the rake',
      'D) The next player to act inherits the blind'
    ],
    correct: 'B',
    explanation: 'Posted blinds remain in the pot. The absent player\'s hand is dealt and folded when action reaches it. The blind is not returned — it is part of the pot and eligible to be won by the active players.'
  },

  // ── HARD (12 questions) ─────────────────────────────────────────────────

  {
    id: 'dp_024', track: 'professional', category: 'dealing_procedure', difficulty: 'hard',
    question: 'You burn and deals the turn, then realizes they forgot to deal the river burn from the previous hand. What is the correct procedure?',
    options: [
      'A) The turn stands — extra burns are irrelevant once the card is dealt',
      'B) Take the turn card back, properly burn, and re-deal the turn',
      'C) The turn is the missing burn card; deal a replacement turn without burning',
      'D) Call the floor — this is a ruling situation'
    ],
    correct: 'A',
    explanation: 'Missing a burn card from a previous street does not affect the current deal. The turn card dealt is the turn. Burn cards exist to protect against edge-marked cards — a missed burn from a prior street has no effect on the current card\'s validity.'
  },

  {
    id: 'dp_025', track: 'professional', category: 'dealing_procedure', difficulty: 'hard',
    question: 'During a 9-handed deal, the dealer miscounts and gives the button position an extra card. The error is found after all players have looked at your cards. What is the ruling?',
    options: [
      'A) The button discards one card face-down without looking',
      'B) The floor randomly selects a card from the button\'s hand to discard',
      'C) The hand is a misdeal — all cards are returned and redealt',
      'D) The player with three cards discards down to two by their own choice'
    ],
    correct: 'C',
    explanation: 'Once players have looked at their cards and an incorrect number of hole cards is discovered, it is a misdeal. The hand cannot be fairly corrected because the affected player has seen their cards and any discard would be advantageous information.'
  },

  {
    id: 'dp_026', track: 'professional', category: 'dealing_procedure', difficulty: 'hard',
    question: 'A dealer deals the river card face-up off the table onto the floor. What is the correct procedure?',
    options: [
      'A) Pick it up and use it as the river — the deal is complete',
      'B) Pick it up, show it to the table, and use it as the river',
      'C) That card is dead; shuffle it back into the deck and deal a new river with a burn',
      'D) Call the floor — a card leaving the table is always a misdeal'
    ],
    correct: 'B',
    explanation: 'A card that falls off the table is still a valid card. It is picked up, shown to all players (to confirm its identity), and used as the river. The card is not shuffled back. No additional burn is required.'
  },

  {
    id: 'dp_027', track: 'professional', category: 'dealing_procedure', difficulty: 'hard',
    question: 'Two all-in players are dealt in. During the deal, one player\'s first hole card is dealt face-up to the table (not a player error). What happens?',
    options: [
      'A) Misdeal — redeal the hand',
      'B) Complete the deal; the exposed card is shown to all, placed as the burn, and a replacement dealt',
      'C) The player with the exposed card plays the hand with one card visible to all',
      'D) The exposed card is used as the first community card'
    ],
    correct: 'B',
    explanation: 'One exposed card during the deal does not cause a misdeal. The deal is completed. The exposed card is acknowledged, placed as the first burn card, and a replacement card is dealt to that player. The hand continues.'
  },

  {
    id: 'dp_028', track: 'professional', category: 'dealing_procedure', difficulty: 'hard',
    question: 'The flop is dealt face-down by mistake (all three cards face-down). What is the correct procedure?',
    options: [
      'A) Turn all three cards face-up — they are the flop',
      'B) Return the three cards, reshuffle, and re-deal with a proper burn',
      'C) Return the three cards to the deck without reshuffling; burn one card and re-deal the flop',
      'D) Use two of the three cards and reshuffle the third back into the deck'
    ],
    correct: 'A',
    explanation: 'If the flop is dealt face-down, simply turn all three cards face-up. They are the legitimate flop — the burn was already done. There is no need to reshuffle or redeal. The cards are live once dealt.'
  },

  {
    id: 'dp_029', track: 'professional', category: 'dealing_procedure', difficulty: 'hard',
    question: 'A player acts on the turn but you realize the turn was dealt without burning. Two players have already acted. What happens?',
    options: [
      'A) The turn is taken back, a proper burn is performed, and action restarts',
      'B) The turn stands — missing a burn does not change the card; action continues from where it is',
      'C) The hand is a misdeal since an improper procedure was used',
      'D) The burn card is retroactively removed from the deck and placed under the pot'
    ],
    correct: 'B',
    explanation: 'Missing a burn card does not invalidate the street. The turn card stands. Action is not rewound. The burn exists to protect against marked cards — missing it is a procedural error that does not change the validity of the card dealt.'
  },

  {
    id: 'dp_030', track: 'professional', category: 'dealing_procedure', difficulty: 'hard',
    question: 'It is discovered mid-hand that the deck has only 51 cards (one is missing). What is the ruling?',
    options: [
      'A) The hand is a misdeal — the deck must be complete',
      'B) Continue the hand if the missing card would not have affected the outcome',
      'C) The floor determines whether the missing card affects the current hand; if not, play continues',
      'D) Immediately stop the hand and retrieve the missing card'
    ],
    correct: 'A',
    explanation: 'If it is discovered that the deck is incomplete, the hand is void and redealt with a complete deck. There is no way to guarantee the missing card did not affect any player\'s holdings or the burn pile, so the hand cannot fairly continue.'
  },

  {
    id: 'dp_031', track: 'professional', category: 'dealing_procedure', difficulty: 'hard',
    question: 'Three all-in players have their cards tabled before the flop. How does the dealer run the board?',
    options: [
      'A) Deal the board normally: burn and flop, burn and turn, burn and river',
      'B) Deal all five community cards at once without burns',
      'C) Deal the board normally; the number of all-in players does not change procedure',
      'D) Burn all three burn cards first, then deal all five community cards'
    ],
    correct: 'C',
    explanation: 'The board is dealt with the standard procedure regardless of how many players are all-in: burn and flop three, burn and turn, burn and river. The presence of all-in players does not change dealing procedure.'
  },

  {
    id: 'dp_032', track: 'professional', category: 'dealing_procedure', difficulty: 'hard',
    question: 'A player who has already folded accidentally sees another player\'s hole card when they lean over. They announce what they saw. What do you do?',
    options: [
      'A) Nothing — folded players are not part of the hand',
      'B) Ask the player who was exposed if they want a ruling',
      'C) Announce the exposed card to all active players immediately',
      'D) Call the floor to determine the impact and appropriate action'
    ],
    correct: 'D',
    explanation: 'When a folded player sees and announces a live player\'s hole card, this is a significant irregularity requiring a floor ruling. The floor determines whether the hand can continue fairly and what if any penalty applies. The dealer calls the floor immediately.'
  },

  {
    id: 'dp_033', track: 'professional', category: 'dealing_procedure', difficulty: 'hard',
    question: 'The board reads K♥ K♦ K♣ K♠. A player turns over A♠ 2♣. What does you announce?',
    options: [
      'A) "Four kings"',
      'B) "Ace high"',
      'C) "Four kings with an ace kicker"',
      'D) "The board plays — four kings with ace kicker"'
    ],
    correct: 'C',
    explanation: 'The correct announcement is "Four kings, ace kicker." The player\'s best five-card hand uses all four kings on the board plus their ace as the fifth card (the kicker). The dealer reads the best possible five-card hand from the player\'s hole cards combined with the board.'
  },

  {
    id: 'dp_034', track: 'professional', category: 'dealing_procedure', difficulty: 'hard',
    question: 'During a hand, two players simultaneously expose their hands — one mid-hand, one at showdown. How do you handle this?',
    options: [
      'A) Both hands are dead — no hand may be exposed during play',
      'B) The mid-hand exposure: show to all active players and continue. The showdown exposure: standard cards-speak ruling applies',
      'C) Both hands are shown to all players immediately',
      'D) The hand exposed mid-play is dead; the showdown hand is live'
    ],
    correct: 'B',
    explanation: 'These are two different situations. A card exposed mid-hand is shown to all active players (per the one-player-to-a-hand rule) and the hand continues. Cards tabled at showdown are read under cards-speak rules. Different procedures apply to each.'
  },

  {
    id: 'dp_035', track: 'professional', category: 'dealing_procedure', difficulty: 'hard',
    question: 'You are pitching cards and the second card for a player lands on top of their first card, obscuring whether they received both. The player claims they only have one card. What is the procedure?',
    options: [
      'A) Take the player\'s word and deal them a second card',
      'B) Count the deck — if 52 minus active players minus burn cards are remaining, the player has two cards',
      'C) Have the player fan their cards face-down to verify the count without revealing them',
      'D) Call the floor to determine if a misdeal has occurred'
    ],
    correct: 'C',
    explanation: 'The dealer should ask the player to fan their cards face-down to count them without revealing the values. If only one card is present, a misdeal or deal-from-deck correction applies. The deck count method also works but is slower. The player\'s claim alone is not sufficient.'
  },

];

export default DEALING_PROCEDURE_QUESTIONS;
